/**
 * Query Keys canónicos y con aislamiento multitenant (tenant-scoped).
 * Garantiza que cada consulta e invalidación en React Query quede aislada por tenantId,
 * evitando fugas de caché y contaminación de datos entre cuentas o sesiones.
 */
import type { DashboardPeriod } from '@/services/dashboardApi'

export const queryKeys = {
  // Canales / WhatsApp
  instances: (tenantId: string | null) => ['wa', 'instances', tenantId ?? 'no-tenant'] as const,
  instance: (tenantId: string | null, id: string) => ['wa', 'instance', tenantId ?? 'no-tenant', id] as const,

  // Contactos
  contacts: (tenantId: string | null) => ['contacts', tenantId ?? 'no-tenant'] as const,
  contact: (tenantId: string | null, id: string) => ['contact', tenantId ?? 'no-tenant', id] as const,

  // Etiquetas
  tags: (tenantId: string | null) => ['tags', tenantId ?? 'no-tenant'] as const,
  conversationTags: (tenantId: string | null, conversationId: string) => ['conversationTags', tenantId ?? 'no-tenant', conversationId] as const,

  // Conversaciones
  conversations: (tenantId: string | null) => ['conversations', tenantId ?? 'no-tenant'] as const,
  conversation: (tenantId: string | null, id: string) => ['conversation', tenantId ?? 'no-tenant', id] as const,
  conversationCounts: (tenantId: string | null) => ['conversations', 'counts', tenantId ?? 'no-tenant'] as const,
  conversationHitl: (tenantId: string | null, id: string) => ['conversation', tenantId ?? 'no-tenant', id, 'hitl-status'] as const,

  // Notas internas
  notes: (tenantId: string | null, conversationId: string) => ['notes', tenantId ?? 'no-tenant', conversationId] as const,

  // Pedidos
  orders: (tenantId: string | null) => ['orders', tenantId ?? 'no-tenant'] as const,
  ordersList: (tenantId: string | null, filters: unknown) =>
    ['orders', tenantId ?? 'no-tenant', 'list', filters] as const,
  order: (tenantId: string | null, id: string) => ['order', tenantId ?? 'no-tenant', id] as const,
  orderChannelInstances: (tenantId: string | null) =>
    ['orders', tenantId ?? 'no-tenant', 'channel-instances'] as const,
  orderContacts: (tenantId: string | null, search: string) =>
    ['orders', tenantId ?? 'no-tenant', 'contacts', search] as const,
  orderProducts: (tenantId: string | null) => ['orders', tenantId ?? 'no-tenant', 'products'] as const,
  orderBotSettings: (tenantId: string | null) => ['orders', tenantId ?? 'no-tenant', 'bot-settings'] as const,
  orderConversation: (tenantId: string | null, id: string) =>
    ['orders', tenantId ?? 'no-tenant', 'conversation', id] as const,

  // Avisos de pedidos a integraciones
  webhookEndpoints: (tenantId: string | null) =>
    ['webhook-endpoints', tenantId ?? 'no-tenant'] as const,
  webhookDeliveries: (tenantId: string | null, endpointId: string, status?: string) =>
    ['webhook-deliveries', tenantId ?? 'no-tenant', endpointId, status ?? 'all'] as const,

  // Configuración de catálogo / promociones / categorías
  categories: (tenantId: string | null) => ['categories', tenantId ?? 'no-tenant'] as const,
  promotions: (tenantId: string | null) => ['promotions', tenantId ?? 'no-tenant'] as const,

  // Facturación y planes del tenant
  billingSummary: (tenantId: string | null) => ['billingSummary', tenantId ?? 'no-tenant'] as const,
  myPaymentReports: (tenantId: string | null) => ['myPaymentReports', tenantId ?? 'no-tenant'] as const,

  // Dashboard
  dashboardStats: (tenantId: string | null, period: DashboardPeriod = 'today') =>
    ['dashboard', 'stats', tenantId ?? 'no-tenant', period] as const,
  dashboardTrends: (tenantId: string | null, days: number) =>
    ['dashboard', 'trends', tenantId ?? 'no-tenant', days] as const,
  warrantyCoverage: (tenantId: string | null) => ['dashboard', 'warranty-coverage', tenantId ?? 'no-tenant'] as const,
  unmetDemand: (tenantId: string | null) => ['dashboard', 'unmet-demand', tenantId ?? 'no-tenant'] as const,
  ordersSummary: (tenantId: string | null) => ['orders', 'summary', tenantId ?? 'no-tenant'] as const,
  closureStats: (tenantId: string | null, days: number) => ['dashboard', 'closure-stats', tenantId ?? 'no-tenant', days] as const,
  pendingBotDoubts: (tenantId: string | null) => ['dashboard', 'pending-bot-doubts', tenantId ?? 'no-tenant'] as const,

  // Configuración usada por el estado de puesta en marcha
  botSettings: (tenantId: string | null) => ['bot-settings', tenantId ?? 'no-tenant'] as const,
  businessHours: (tenantId: string | null) => ['business-hours', tenantId ?? 'no-tenant'] as const,
  faqs: (tenantId: string | null) => ['faqs', tenantId ?? 'no-tenant'] as const,
} as const
